import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetUserByIdQuery,
  useUpdateUserMutation,
} from '../slices/usersApiSlice';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  LinearProgress,
  Switch,
} from '@mui/material';
import { useSelector } from 'react-redux';
import Alerton from '../components/Alerton';
import ScreenContainer from '../components/ScreenContainer';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import {
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AppSettingsAltOutlinedIcon from '@mui/icons-material/AppSettingsAltOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import AppsIcon from '@mui/icons-material/Apps';

const UserScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const { id } = useParams();
  const [message, setMessage] = useState('');
  const [userData, setUserData] = useState({});
  const [alltypes, setAllTypes] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [permissionsRegistry, setPermissionsRegistry] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [checkedRoles, setCheckedRoles] = useState([]);
  const [loginInput, setLoginInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [readOnly, setReafOnly] = useState(false);
  const [onlyStages, setOnlyStages] = useState(false);
  const [types, setTypes] = useState([]);
  const [reports, setReports] = useState([]);

  const { data, refetch, isLoading, isError } = useGetUserByIdQuery(id, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [updateUser, { isLoading: isLoadingUserUpdating }] =
    useUpdateUserMutation();

  const handleToggleRoles = (value) => () => {
    const currentIndex = checkedRoles.indexOf(value);
    const newChecked = [...checkedRoles];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedRoles(newChecked);
  };

  useEffect(() => {
    // console.log('UserScreen data', data);
    if (data?.row) setUserData(data?.row);
    if (data?.types) setAllTypes(data.types);
    if (data?.roles) setAllRoles(data.roles);
    if (data?.reports) setAllReports(data.reports);
    if (data?.row?.roles) setCheckedRoles(data.row.roles.split(','));
    if (data?.permissionsRegistry)
      setPermissionsRegistry(data.permissionsRegistry);
    // if (data?.row?.permissions) setUserPermissions(data.row.permissions);
    if (data?.row?.permissions) {
      // ГЛУБОКАЯ копия!
      setUserPermissions(JSON.parse(JSON.stringify(data.row.permissions)));
      // или structuredClone(data.row.permissions)
    }
  }, [data]);

  useEffect(() => {
    if (data?.row) {
      const {
        login,
        name,
        password,
        isAdmin,
        readOnly,
        onlyStages,
        types,
        reports,
      } = data.row;
      setLoginInput(login || '');
      setNameInput(name || '');
      setPasswordInput('');
      setIsAdmin(isAdmin || false);
      setReafOnly(readOnly || false);
      setOnlyStages(onlyStages || false);
      setTypes(types || []);
      setReports(reports || []);
    }
  }, [data?.row]);

  const handleCloseMessage = () => {
    setMessage('');
  };

  // console.log('userPermissions', userPermissions);

  // const handlePermissionToggle = (resource, action) => (e) => {
  //   const checked = e.target.checked;
  //   const prev = userPermissions[resource] || {};
  //   const next = {
  //     ...userPermissions,
  //     [resource]: { ...prev, [action]: checked },
  //   };
  //   setUserPermissions(next);
  //   // тут можно сразу дергать setPermissions API или копить до submit
  // };

  const handlePermissionToggle = (resource, fullPath) => (e) => {
    // fullPath = 'stages.create' или 'login'
    const checked = e.target.checked;
    setUserPermissions((prev) => {
      const parts = fullPath.split('.');
      const newPerms = { ...prev };
      let current = newPerms[resource] || {};

      if (parts.length === 1) {
        current[parts[0]] = checked;
      } else {
        // Nested: directories.stages.create → directories.stages = {create: true}
        const subRes = parts[0];
        const action = parts[1];
        current[subRes] = { ...current[subRes], [action]: checked };
      }

      newPerms[resource] = current;
      return newPerms;
    });
  };

  const handleTypeCheck = (id) => {
    if (types.includes(id)) {
      setTypes(types.filter((type) => type !== id));
    } else {
      setTypes([...types, id]);
    }
  };

  const handleReportCheck = (id) => {
    if (reports.includes(id)) {
      setReports(reports.filter((report) => report !== id));
    } else {
      setReports([...reports, id]);
    }
  };

  const submitHandler = async () => {
    try {
      let setData = {};
      if (userInfo?.isAdmin && !userInfo?.readOnly) {
        setData = {
          id: id,
          name: nameInput,
          login: loginInput,
          password: passwordInput,
          isAdmin: isAdmin,
          readOnly: readOnly,
          onlyStages: onlyStages,
          types: types,
          reports: reports,
          roles: checkedRoles.join(','),
          permissions: userPermissions,
        };
      } else {
        setData = {
          id: id,
          name: nameInput,
          login: loginInput,
          password: passwordInput,
        };
      }

      await updateUser(setData).unwrap();
      refetch();
    } catch (error) {
      console.log(error);
      setMessage(error?.data?.message || error.error);
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  } else if (isError) {
    return (
      <Alert severity='warning'>
        {isError?.data?.message || isError.error}
      </Alert>
    );
  }

  const buttons = [
    {
      title: 'Сохранить',
      onClick: submitHandler,
      type: 'icon',
      icon: <SaveIcon />,
      variant: 'outlined',
      color: 'success',
      xs: 2,
      position: 'left',
    },
    {
      title: 'Закрыть',
      onClick: () => {
        navigate(-1);
      },
      type: 'icon',
      icon: <CloseIcon />,
      xs: 2,
      position: 'right',
    },
  ];

  // const ActionsPermissions = () => {
  //   return (
  //     <Box sx={{ mt: 3, p: 0 }}>
  //       <Typography
  //         variant='button'
  //         display='block'
  //         gutterBottom
  //         sx={{ m: 0, p: 0 }}
  //       >
  //         {isAdmin ? 'Роли' : 'Мои права'}
  //       </Typography>
  //       <List
  //         dense={true}
  //         sx={{
  //           width: '100%',
  //           bgcolor: 'background.paper',
  //           m: 0,
  //           p: 0,
  //         }}
  //       >
  //         {Array.isArray(allRoles) &&
  //           allRoles.map((role, index) => {
  //             const labelId = `checkbox-list-secondary-label-${role?.code}`;
  //             return (
  //               <ListItem
  //                 key={index}
  //                 secondaryAction={
  //                   <Checkbox
  //                     edge='end'
  //                     onChange={handleToggleRoles(role.code)}
  //                     checked={checkedRoles.includes(role.code)}
  //                     inputProps={{ 'aria-labelledby': labelId }}
  //                     tabIndex={-1}
  //                     disableRipple
  //                   />
  //                 }
  //                 disablePadding
  //               >
  //                 <ListItemButton>
  //                   <ListItemText id={labelId} primary={`${role?.title}`} />
  //                 </ListItemButton>
  //               </ListItem>
  //             );
  //           })}
  //       </List>
  //     </Box>
  //   );
  // };

  // const DirectoriesPermissions = () => {
  //   return (
  //     <Box sx={{ mt: 3, p: 0 }}>
  //       <Typography
  //         variant='button'
  //         display='block'
  //         gutterBottom
  //         sx={{ m: 0, p: 0 }}
  //       >
  //         Права доступа к справочникам
  //       </Typography>

  //       {Object.entries(permissionsRegistry).map(([resource, cfg]) => (
  //         <Box key={resource} sx={{ mt: 1 }}>
  //           <Typography variant='subtitle2'>{cfg.label}</Typography>
  //           <List dense sx={{ m: 0, p: 0 }}>
  //             {Object.entries(cfg.actions).map(([actionKey, actionLabel]) => {
  //               const checked = Boolean(
  //                 userPermissions?.[resource]?.[actionKey]
  //               );
  //               const id = `perm-${resource}-${actionKey}`;
  //               return (
  //                 <ListItem
  //                   key={id}
  //                   disablePadding
  //                   secondaryAction={
  //                     <Checkbox
  //                       edge='end'
  //                       checked={checked}
  //                       onChange={handlePermissionToggle(resource, actionKey)}
  //                       inputProps={{
  //                         'aria-labelledby': id,
  //                       }}
  //                     />
  //                   }
  //                 >
  //                   <ListItemButton>
  //                     <ListItemText id={id} primary={actionLabel} />
  //                   </ListItemButton>
  //                 </ListItem>
  //               );
  //             })}
  //           </List>
  //         </Box>
  //       ))}
  //     </Box>
  //   );
  // };

  const ActionsPermissions = () => (
    <Box sx={{ mt: 3, p: 0 }}>
      <Typography variant='button' display='block' gutterBottom>
        Роли
      </Typography>
      <List dense>
        {Object.entries(permissionsRegistry.roles?.actions || {}).map(
          ([roleKey, roleLabel]) => {
            const checked = Boolean(userPermissions?.roles?.[roleKey]);
            return (
              <ListItem
                key={roleKey}
                secondaryAction={
                  <Checkbox
                    onChange={handlePermissionToggle('roles', roleKey)}
                    checked={checked}
                  />
                }
                disablePadding
              >
                <ListItemButton>
                  <ListItemText primary={roleLabel} />
                </ListItemButton>
              </ListItem>
            );
          },
        )}
      </List>
    </Box>
  );

  const DirectoriesPermissions = () => {
    return (
      <Box sx={{ mt: 3, p: 0 }}>
        <Typography variant='button' display='block' gutterBottom>
          Права доступа к справочникам
        </Typography>

        {/* Группа directories */}
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle1'>Справочники</Typography>
          {Object.entries(
            permissionsRegistry.directories?.subResources || {},
          ).map(([dirKey, dirCfg]) => (
            <Box key={dirKey} sx={{ mt: 1, pl: 2 }}>
              <Typography variant='subtitle2'>{dirCfg.label}</Typography>
              <List dense sx={{ m: 0, p: 0 }}>
                {Object.entries(
                  dirCfg.actions || permissionsRegistry.directories.actions,
                ).map(([actionKey, actionLabel]) => {
                  const checked = Boolean(
                    userPermissions?.directories?.[dirKey]?.[actionKey],
                  );
                  return (
                    <ListItem
                      key={`${dirKey}-${actionKey}`}
                      disablePadding
                      secondaryAction={
                        <Checkbox
                          edge='end'
                          checked={checked}
                          onChange={handlePermissionToggle(
                            'directories',
                            `${dirKey}.${actionKey}`,
                          )} // 'directories.stages.create'
                          inputProps={{
                            'aria-labelledby': `${dirKey}-${actionKey}`,
                          }}
                        />
                      }
                    >
                      <ListItemButton>
                        <ListItemText primary={actionLabel} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const TypesPermission = () => {
    return (
      <Box sx={{ mt: 3, p: 0 }}>
        <Typography
          variant='button'
          display='block'
          gutterBottom
          sx={{ m: 0, p: 0 }}
        >
          Доступные типы объектов:
        </Typography>
        <List
          dense={true}
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            m: 0,
            p: 0,
          }}
        >
          {Array.isArray(alltypes) &&
            alltypes.length > 0 &&
            alltypes.map((type) => {
              const labelId = `checkbox-list-label-${type._id}`;

              return (
                <ListItem
                  key={type._id}
                  secondaryAction={
                    <Checkbox
                      edge='end'
                      checked={types?.includes(type._id) || false}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                      onChange={() => handleTypeCheck(type._id)}
                    />
                  }
                  disablePadding
                >
                  <ListItemButton role={undefined} dense>
                    <ListItemText id={labelId} primary={type?.title} />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Box>
    );
  };

  const ReportsPermissions = () => {
    // if (!Array.isArray(allReports) || allReports.length === 0) return null;

    return (
      <Box sx={{ mt: 3, p: 0 }}>
        <Typography
          variant='button'
          display='block'
          gutterBottom
          sx={{ m: 0, p: 0 }}
        >
          Отчеты:
        </Typography>
        <List
          dense={true}
          sx={{
            width: '100%',
            // maxWidth: 360,
            bgcolor: 'background.paper',
            m: 0,
            p: 0,
          }}
        >
          {Array.isArray(allReports) &&
            allReports.length > 0 &&
            allReports.map((report) => {
              const labelId = `report-checkbox-list-label-${report.code}`;

              return (
                <ListItem
                  key={report.code}
                  secondaryAction={
                    <Checkbox
                      edge='end'
                      checked={reports?.includes(report.code) || false}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                      onChange={() => handleReportCheck(report.code)}
                    />
                  }
                  disablePadding
                >
                  <ListItemButton role={undefined} dense>
                    <ListItemText id={labelId} primary={report?.title} />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>
      </Box>
    );
  };

  const OtherPermissions = () => {
    const otherResources = ['crm', 'reports', 'news', 'notes', 'tasks']; // Из registry без directories/roles/app

    return (
      <Box sx={{ mt: 3, p: 0 }}>
        {/* <Typography variant='button' display='block' gutterBottom>
          CRM
        </Typography> */}
        {otherResources.map(
          (resource) =>
            permissionsRegistry[resource] && (
              <Box key={resource} sx={{ mb: 2 }}>
                <Typography variant='subtitle1'>
                  {permissionsRegistry[resource].label}
                </Typography>
                <List dense sx={{ m: 0, p: 0 }}>
                  {Object.entries(permissionsRegistry[resource].actions).map(
                    ([actionKey, actionLabel]) => {
                      const checked = Boolean(
                        userPermissions?.[resource]?.[actionKey],
                      );
                      return (
                        <ListItem
                          key={actionKey}
                          disablePadding
                          secondaryAction={
                            <Checkbox
                              edge='end'
                              checked={checked}
                              onChange={handlePermissionToggle(
                                resource,
                                actionKey,
                              )}
                            />
                          }
                        >
                          <ListItemButton>
                            <ListItemText primary={actionLabel} />
                          </ListItemButton>
                        </ListItem>
                      );
                    },
                  )}
                </List>
              </Box>
            ),
        )}
      </Box>
    );
  };

  const AppPermissions = () => {
    const includedResources = ['app', 'dashboard'];

    return (
      <>
        {includedResources.map(
          (resource) =>
            permissionsRegistry[resource] && (
              <Box key={resource} sx={{ mt: 3, p: 0 }}>
                <Typography variant='button' display='block' gutterBottom>
                  {permissionsRegistry[resource].label}
                </Typography>
                <List dense sx={{ m: 0, p: 0 }}>
                  {Object.entries(
                    permissionsRegistry[resource].actions || {},
                  ).map(([actionKey, actionLabel]) => {
                    const checked = Boolean(
                      userPermissions?.[resource]?.[actionKey],
                    );
                    return (
                      <ListItem
                        key={actionKey}
                        disablePadding
                        secondaryAction={
                          <Checkbox
                            edge='end'
                            checked={checked}
                            onChange={handlePermissionToggle(
                              resource,
                              actionKey,
                            )}
                            inputProps={{
                              'aria-labelledby': `app-${actionKey}`,
                            }}
                          />
                        }
                      >
                        <ListItemButton>
                          <ListItemText
                            id={`${resource}-${actionKey}`}
                            primary={actionLabel}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ),
        )}
      </>
    );
  };

  const tabs = {
    buttons: [
      { label: 'Воронки', value: 'funnels', icon: <FilterAltOutlinedIcon /> },
      { label: 'Отчеты', value: 'reports', icon: <AssessmentOutlinedIcon /> },
      // { label: 'Роли', value: 'roles' },
      {
        label: 'Справочники',
        value: 'directories',
        icon: <CategoryOutlinedIcon />,
      },
      { label: 'Система', value: 'app', icon: <AppSettingsAltOutlinedIcon /> }, // Новая для app
      { label: 'CRM', value: 'other', icon: <AppsIcon /> },
    ],
    position: 'bottom',
    panels: [
      {
        value: 'Воронки',
        content: <TypesPermission />,
        visible: !!alltypes?.length,
      },
      {
        value: 'Отчеты',
        content: <ReportsPermissions />,
        visible: !!allReports?.length,
      },
      // { value: 'Роли', content: <ActionsPermissions />, visible: true },
      {
        value: 'Справочники',
        content: <DirectoriesPermissions />,
        visible: !!permissionsRegistry?.directories,
      },
      {
        value: 'Система',
        content: <AppPermissions />,
        visible: !!permissionsRegistry?.app,
      }, // Новая панель
      { value: 'Прочие', content: <OtherPermissions />, visible: true },
    ],
  };

  return (
    <>
      {isLoadingUserUpdating && <LinearProgress />}
      <ScreenContainer buttons={buttons} tabs={tabs}>
        <Card>
          <CardHeader title={'Сотрудник'} />
          <CardContent sx={{ pt: 0 }}>
            {userData && (
              <>
                <form noValidate autoComplete='off'>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <strong>Имя: </strong>
                      <TextField
                        size='small'
                        fullWidth
                        id='name'
                        value={nameInput || ''}
                        onChange={(e) => setNameInput(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <strong>Логин: </strong>
                      <TextField
                        size='small'
                        fullWidth
                        id='login'
                        value={loginInput || ''}
                        onChange={(e) => setLoginInput(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <strong>Пароль: </strong>
                      <TextField
                        size='small'
                        fullWidth
                        id='login'
                        value={passwordInput || ''}
                        onChange={(e) => setPasswordInput(e.target.value)}
                      />
                    </Grid>
                    {userInfo?.isAdmin && !userInfo?.readOnly && (
                      <Grid item xs={12}>
                        <strong>Администратор: </strong>
                        <Switch
                          onChange={(e) => setIsAdmin(e.target.checked)}
                          checked={isAdmin}
                        />
                      </Grid>
                    )}
                  </Grid>
                </form>
              </>
            )}
          </CardContent>
        </Card>
      </ScreenContainer>
      <Alerton message={message} handleCloseMessage={handleCloseMessage} />
    </>
  );
};

export default UserScreen;
